import get from 'lodash/get';
import { select, put, call, takeLatest, all } from 'redux-saga/effects';
import API from 'services/api';
import {
  siteDataGridActions,
  siteVisitorActions,
  siteFilterActions,
  sitesNS,
  siteSelectors,
} from 'containers/Pages/Locations/Sites';
import { getSecurityId, getUserId } from 'reducers/users';
import { setLoading, showSnackbar, toggleModal } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { transformSiteForm } from 'utils/DataTransform/Sites/SiteForm';
import { actions as globalActions } from 'slices/globalOptions';
import { actions as embeddedFormActions } from 'modules/embeddedForms/reducer';
import { v4 } from 'uuid';
import { MEDIA_LOCATION } from 'services/media';
import { isBase64 } from 'utils/checkImage';
import isEmpty from 'lodash/isEmpty';
const { setGlobalSitesList } = globalActions;

export function* mediaUploadImages(surface) {
  try {
    if (surface && Object.hasOwnProperty.call(surface, 'url')) {
      let { url } = surface;
      if (isBase64(url)) {
        const mediaId = v4();
        const userId = yield select(getUserId);

        yield call(API.uploadMediaItem, {
          userId,
          mediaId,
          data: {
            service: 'ASSET',
            base64: url,
          },
        });

        url = `${MEDIA_LOCATION}/${userId}/${mediaId}/image`;
      }

      surface.imageUrl = url;
    }

    return surface;
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
    throw err;
  }
}

export function* fetchSitesRequest({ filters, from, fetchAll, allUnarchived }) {
  try {
    const securityCompanyId = yield select(getSecurityId);
    const { data } = yield call(API.fetchSitesList, {
      securityCompanyId,
      from,
      fetchAll,
      ...(allUnarchived && { allUnarchived }),
      ...filters,
    });

    if (data.data != null) {
      const totalEntries = get(data, 'meta.total');
      return { list: data.data, totalEntries };
    }
    return false;
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
    return false;
  }
}

export function* fetchSitesListSaga({ payload }) {
  try {
    const { filters, from } = payload;

    const filterKeyStatus = yield select(siteSelectors.getSiteFilterKey);
    const forceRefresh = yield select(siteSelectors.getForceRefresh);
    const getSiteList = yield select(siteSelectors.getSiteListRaw);
    // if firebase sends a notification or workOrderList is empty or filter key is pressed or pagination is increased
    if (
      isEmpty(getSiteList) ||
      forceRefresh ||
      filterKeyStatus ||
      (from === 0 && !isEmpty(getSiteList)) ||
      (from > 0 && !isEmpty(getSiteList))
    ) {
      const data = yield call(fetchSitesRequest, { filters, from });
      if (data) {
        const { list, totalEntries } = data;
        yield put(siteDataGridActions.setGridList({ list, totalEntries }));
      }
    }
    yield put(siteDataGridActions.setForceRefresh(false));
    yield put(siteFilterActions.setFilterKey(false));
  } catch (error) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(error),
      }),
    );
  }
}

export function* fetchSiteDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;

    const [site, visitor] = yield all([
      call(API.fetchSiteDetails, id),
      call(API.getVisitList, { siteId: id }),
    ]);

    if (site.data.data != null) {
      yield put(
        siteDataGridActions.setGridRowDetails({ details: site.data.data }),
      );
      yield put(siteVisitorActions.setVisitors(visitor.data.data));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* createSiteRequest({ site }) {
  try {
    const sitePayload = transformSiteForm(site);
    const { data } = yield call(API.createSite, sitePayload);
    /**
     * TODO: disabled for now
     */
    // if (data.data) {
    //   if (patrolData) {

    //     yield put(
    //       createPatrolDe(
    //         transformPatrolWithNewSite(patrolData, siteData),
    //         false,
    //       ),
    //     );
    //   }
    // }
    return data.data;
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
    return false;
  }
}

export function* createSiteSaga({ payload }) {
  const { site, patrolData, embeddedForm } = payload;
  try {
    yield put(setLoading(true));

    // only if multiple images exist
    if (site.surfaces && site.surfaces.length > 0) {
      const { surfaces } = site;
      const surfacesPayload = yield all(
        surfaces.map(surface => call(mediaUploadImages, surface)),
      );
      site['surfaces'] = surfacesPayload;
    }

    const siteResponse = yield call(createSiteRequest, {
      site,
      patrolData,
    });
    if (siteResponse && embeddedForm) {
      yield put(
        embeddedFormActions.setEmbeddedFormDetails({
          formType: sitesNS,
          details: siteResponse,
        }),
      );
      // pass this info into the embedded form state
    }
    if (siteResponse && !embeddedForm) {
      yield put(toggleModal(sitesNS));
    }
    yield put(setLoading(false));
  } catch (error) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(error),
      }),
    );
  }
}
export function* updateSiteSaga({ payload }) {
  const { id, site } = payload;
  try {
    yield put(setLoading(true));
    // only if multiple images exist
    if (site.surfaces && site.surfaces.length > 0) {
      const { surfaces } = site;
      const surfacesPayload = yield all(
        surfaces.map(surface => call(mediaUploadImages, surface)),
      );
      site['surfaces'] = surfacesPayload;
    }

    const sitePayload = transformSiteForm(site);

    const { status } = yield call(API.updateSite, id, sitePayload);

    if (status === 200) {
      yield call(fetchSiteDetailsSaga, { payload: { id } });
      yield put(toggleModal(sitesNS));
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* fetchGlobalSitesList({ payload }) {
  try {
    yield put(siteDataGridActions.setForceRefresh(false));
    yield put(siteFilterActions.setFilterKey(false));
    const { filters, from, fetchAll, allUnarchived } = payload;
    const data = yield call(fetchSitesRequest, {
      filters,
      from,
      fetchAll,
      allUnarchived,
    });
    if (data) {
      yield put(
        setGlobalSitesList({
          list: data.list,
          totalEntries: data.totalEntries,
        }),
      );
    }
  } catch (error) {
    showSnackbar({
      typeOfSnackbar: 'error',
      message: errorHandler(error),
    });
  }
}
export function* sitesSagas() {
  yield takeLatest(siteDataGridActions.fetchGridList, fetchSitesListSaga);
  yield takeLatest(
    siteDataGridActions.fetchGridRowDetails,
    fetchSiteDetailsSaga,
  );
  yield takeLatest(siteDataGridActions.createRowEntry, createSiteSaga);
  yield takeLatest(siteDataGridActions.updateRowEntry, updateSiteSaga);
  yield takeLatest(globalActions.fetchGlobalSitesList, fetchGlobalSitesList);
}
